import { useEffect, useRef, useState } from "react";

/** Sync with PatientBottomNav.scss / PatientAppShell — chrome hide on scroll (mobile / tablet) */
export const PATIENT_SHELL_SCROLL_HIDE_VIEWPORT_MAX_PX = 1199;

const SCROLL_TOP_REVEAL_PX = 40;
const SCROLL_DELTA_PX = 6;

/**
 * Hide-on-scroll for patient top app bar + bottom tab bar (directional scroll, near-top reveal).
 */
export function usePatientShellScrollHide(pathname) {
  const [scrollHidden, setScrollHidden] = useState(false);
  const lastScrollY = useRef(typeof window !== "undefined" ? window.scrollY : 0);

  useEffect(() => {
    setScrollHidden(false);
    lastScrollY.current = typeof window !== "undefined" ? window.scrollY : 0;
  }, [pathname]);

  useEffect(() => {
    const mqNav = window.matchMedia(`(max-width: ${PATIENT_SHELL_SCROLL_HIDE_VIEWPORT_MAX_PX}px)`);
    const mqReduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const readY = () => window.scrollY ?? document.documentElement.scrollTop ?? 0;

    const onScroll = () => {
      if (!mqNav.matches || mqReduce.matches) {
        setScrollHidden(false);
        return;
      }
      const y = readY();
      const prev = lastScrollY.current;

      if (y <= SCROLL_TOP_REVEAL_PX) {
        setScrollHidden(false);
      } else if (y > prev + SCROLL_DELTA_PX) {
        setScrollHidden(true);
      } else if (y < prev - SCROLL_DELTA_PX) {
        setScrollHidden(false);
      }
      lastScrollY.current = y;
    };

    const onMqNav = () => {
      setScrollHidden(false);
      lastScrollY.current = readY();
    };

    const onMqReduce = () => setScrollHidden(false);

    mqNav.addEventListener("change", onMqNav);
    mqReduce.addEventListener("change", onMqReduce);
    window.addEventListener("scroll", onScroll, { passive: true });

    onScroll();

    return () => {
      mqNav.removeEventListener("change", onMqNav);
      mqReduce.removeEventListener("change", onMqReduce);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return scrollHidden;
}
